import React from "react";
import { ArrowLeft, Loader2, Save, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  COMPACT_TOOLBAR_BUTTON_PRIMARY,
  COMPACT_TOOLBAR_BUTTON_SECONDARY,
} from "./compact-toolbar-button-classes";

export interface CompactFormActionButtonProps {
  label: string;
  /** Texto mientras carga (ej. «Guardando…»). Si no se define, se mantiene `label`. */
  loadingLabel?: string;
  onClick?: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  /** Alias de `isLoading` para el botón submit. */
  isSubmitting?: boolean;
  type?: "button" | "submit";
  /** Icono en estado idle. Por defecto: Save (primary), ArrowLeft (secondary). */
  icon?: React.ReactNode;
  hideIcon?: boolean;
  variant: "secondary" | "primary";
  /** Override del icono por defecto según `variant`. */
  defaultIcon?: LucideIcon;
}

/** @deprecated Use `CompactFormActionButtonProps` — alias para API declarativa del footer. */
export type CompactFormFooterButtonProps = Omit<
  CompactFormActionButtonProps,
  "variant" | "defaultIcon"
>;

export interface CompactFormFooterProps {
  /** Acción izquierda (Cancelar). */
  cancel?: CompactFormFooterButtonProps;
  /** Acción secundaria opcional (centro, ej. Guardar borrador). */
  secondary?: CompactFormFooterButtonProps;
  /** Acción primaria derecha (Submit). */
  submit?: CompactFormFooterButtonProps;
  /** Escape hatch: acciones custom dentro de `.compact-toolbar-actions`. */
  children?: React.ReactNode;
  className?: string;
  /** `dialog` aplica `compact-form-footer--dialog` (footer sticky en modal). */
  placement?: "page" | "dialog";
}

export const CompactFormActionButton: React.FC<CompactFormActionButtonProps> = ({
  label,
  loadingLabel,
  onClick,
  disabled = false,
  isLoading = false,
  isSubmitting = false,
  type = "button",
  icon,
  hideIcon = false,
  variant,
  defaultIcon,
}) => {
  const loading = isLoading || isSubmitting;
  const buttonClass =
    variant === "primary"
      ? COMPACT_TOOLBAR_BUTTON_PRIMARY
      : COMPACT_TOOLBAR_BUTTON_SECONDARY;
  const displayLabel = loading && loadingLabel ? loadingLabel : label;
  const DefaultIcon =
    defaultIcon ?? (variant === "primary" ? Save : ArrowLeft);

  const iconNode = hideIcon ? null : loading ? (
    <Loader2 className="h-3.5 w-3.5 animate-spin" />
  ) : (
    (icon ?? <DefaultIcon className="h-3.5 w-3.5" />)
  );

  return (
    <Button
      type={type}
      size="xs"
      variant={variant === "primary" ? "default" : "outline"}
      className={buttonClass}
      onClick={onClick}
      disabled={disabled || loading}
    >
      {iconNode}
      {displayLabel}
    </Button>
  );
};

export const CompactFormFooter: React.FC<CompactFormFooterProps> = ({
  cancel,
  secondary,
  submit,
  children,
  className,
  placement = "page",
}) => {
  return (
    <div
      className={cn(
        "compact-form-footer",
        placement === "dialog" && "compact-form-footer--dialog",
        className,
      )}
    >
      <div className="compact-toolbar-actions">
        {children ?? (
          <>
            {cancel ? (
              <CompactFormActionButton
                variant="secondary"
                defaultIcon={ArrowLeft}
                type="button"
                {...cancel}
              />
            ) : null}
            {secondary ? (
              <CompactFormActionButton
                variant="secondary"
                defaultIcon={Save}
                type="button"
                {...secondary}
              />
            ) : null}
            {submit ? (
              <CompactFormActionButton
                variant="primary"
                defaultIcon={Save}
                type="submit"
                {...submit}
              />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};
